import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import SearchResults from '../../../../common/components/search-results/SearchResults';
import NoResultsFullView from '../no-results-full-view/NoResultsFullView';
import AddPlaceControl from '../../../../common/components/add-place-control/AddPlaceControl';
import Responsive, {
  useMobile,
} from '../../../../common/components/responsive/Responsive';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import { selectSuggestedFeatures } from '../../../../common/store/search';
import { activeItemChanged } from '../../../item-details/store';
import {
  clearFormState,
  clearLastSentEditMetadata,
  clearRoutablePoints,
  longPressLocationChanged,
} from '../../../../common/store/item-edit';
import { selectStyleSelectorShown } from '../../../../common/store/ui';

const getElementOuterHeight = (el) => {
  const style = (!!window.getComputedStyle as boolean)
    ? getComputedStyle(el, null)
    : el.currentStyle;
  const marginTop = style.marginTop
    ? parseInt(style.marginTop.match(/^(\d)*/g)[0])
    : 0;
  const marginBottom = style.marginBottom
    ? parseInt(style.marginBottom.match(/^(\d)*/g)[0])
    : 0;

  return el.offsetHeight + marginTop + marginBottom;
};

export interface ResultsListPageProps {
  className?: string;
}

const ResultsListPage = ({ className }: ResultsListPageProps) => {
  const isMobile = useMobile();
  const dispatch = useDispatch();

  const [minHeight, setMinHeight] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();

  const features = useSelector(selectSuggestedFeatures);
  const layoutRef = useRef<HTMLElement>();
  useLayoutEffect(() => {
    if (!isMobile) {
      setMinHeight(undefined);
      setMaxHeight(undefined);
      return;
    }

    if (layoutRef.current) {
      const layoutHeader = layoutRef.current.getElementsByClassName(
        'wizard-layout__header',
      )[0] as HTMLElement;
      const layoutHeaderHeight = layoutHeader.offsetHeight;

      const searchResults =
        layoutRef.current.getElementsByClassName('search-results')[0];
      const searchResultsScrollHeight = searchResults
        ? Array.from(searchResults.children).reduce(
            (acc, child) => acc + getElementOuterHeight(child),
            0,
          )
        : 200; // default height when there is no results

      const addPlaceBtn = layoutRef.current.getElementsByClassName(
        'search-results__add-place-button',
      )[0] as HTMLElement;
      const addPlaceBtnHeight = addPlaceBtn?.offsetHeight || 0;

      setMinHeight(layoutHeaderHeight + addPlaceBtnHeight);
      setMaxHeight(
        layoutHeaderHeight + addPlaceBtnHeight + searchResultsScrollHeight,
      );
    }
  }, [features, isMobile]);

  useMount(() => {
    dispatch(activeItemChanged({ item: null }));
    dispatch(clearFormState());
    dispatch(clearRoutablePoints());
    dispatch(clearLastSentEditMetadata());
    dispatch(longPressLocationChanged(null));
  });

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  return (
    <WizardLayout
      title={isMobile ? 'Search results' : null}
      hasCloseButton={isMobile}
      resizable={isMobile}
      resizeOptions={{ minHeight, maxHeight }}
      ref={layoutRef}
    >
      <SearchResults
        className={className}
        showAddPlaceWhen="always"
        showIndexes={true}
        noResults={
          <NoResultsFullView className="wizard__no-results-full-view" />
        }
      />
      <Responsive mobile={false}>
        <AddPlaceControl className="wizard__add-place-control" />
      </Responsive>
    </WizardLayout>
  );
};

export default ResultsListPage;
